import React, { useEffect, useState } from 'react'
import { gql, useLazyQuery, useMutation } from '@apollo/client'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Skeleton, TextField, Typography,
} from '@mui/material'
import { getStripeRefundPreview } from '../../apollo/queries'
import { refundStripeOrder } from '../../apollo/mutations'
import { color, focusRing, radius, usd } from './tokens'

const GET_REFUND_PREVIEW = gql`${getStripeRefundPreview}`
const REFUND = gql`${refundStripeOrder}`

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: radius.control,
    '&.Mui-focused fieldset': { borderColor: color.accent, borderWidth: 1, boxShadow: `0 0 0 3px ${color.accentSoft}` },
  },
  '& label.Mui-focused': { color: color.accent },
}

function Row({ label, value, strong }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.35 }}>
      <Typography sx={{ fontSize: 14, color: strong ? color.text : color.muted, fontWeight: strong ? 600 : 400 }}>{label}</Typography>
      <Typography sx={{ fontSize: 14, color: color.text, fontWeight: strong ? 700 : 500, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
    </Box>
  )
}

// Refunds from the dashboard come out of the restaurant's share, so the dialog
// shows exactly what that means before anything is sent to Stripe.
export default function RefundDialog({ order, onClose, onDone }) {
  const [amount, setAmount] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState(null)
  const [loadPreview, { data: previewData, loading: previewLoading, error: previewError }] = useLazyQuery(GET_REFUND_PREVIEW, {
    fetchPolicy: 'network-only',
  })
  const [refund, { loading: refunding }] = useMutation(REFUND)

  useEffect(() => {
    if (!order) return
    setAmount(Number(order.refundable).toFixed(2))
    setNote('')
    setError(null)
  }, [order])

  const parsed = parseFloat(amount)
  const valid = order && parsed > 0 && parsed <= Number(order.refundable) + 0.001

  // Re-price the preview as the amount is typed, once it settles.
  useEffect(() => {
    if (!valid) return undefined
    const t = setTimeout(() => loadPreview({ variables: { orderId: order.orderId, amount: parsed } }), 350)
    return () => clearTimeout(t)
  }, [amount, valid])

  const preview = valid ? previewData?.stripeRefundPreview : null

  const submit = async () => {
    setError(null)
    try {
      const res = await refund({
        variables: { orderId: order.orderId, amount: parsed, chargeTo: 'RESTAURANT', note: note || null },
      })
      const r = res?.data?.refundStripeOrder
      if (!r?.ok) {
        setError(r?.message || 'The refund did not go through.')
        return
      }
      const parts = [`Refunded ${usd(r.refunded)} to the customer`]
      if (r.reversed > 0) parts.push(`${usd(r.reversed)} taken back from your share`)
      if (r.debt > 0) parts.push(`${usd(r.debt)} will come out of your next payouts`)
      onDone(`${parts.join(', ')}.`)
    } catch (e) {
      setError(e.message)
    }
  }

  return (
    <Dialog
      open={!!order}
      onClose={refunding ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: { borderRadius: radius.card, boxShadow: '0 12px 32px rgba(26,31,54,0.18)' } }}
    >
      <DialogTitle sx={{ fontSize: 19, fontWeight: 600, color: color.text, pb: 0.5 }}>
        Refund order #{order?.orderNumber || order?.orderId}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 14, color: color.muted, mb: 2.5, lineHeight: 1.55 }}>
          The customer paid {usd(order?.customerTotal)}. Up to {usd(order?.refundable)} can still be refunded.
          Refunds come out of your share of this order.
        </Typography>
        <TextField
          label="Refund amount"
          type="number"
          size="small"
          fullWidth
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: 0.01, max: order?.refundable }}
          error={amount !== '' && !valid}
          helperText={amount !== '' && !valid ? `Enter an amount up to ${usd(order?.refundable)}` : ' '}
          sx={fieldSx}
        />
        <TextField
          label="Note (optional)"
          size="small"
          fullWidth
          value={note}
          onChange={(e) => setNote(e.target.value)}
          sx={{ ...fieldSx, mt: 1 }}
        />
        <Box sx={{ mt: 2, p: 2, borderRadius: '10px', bgcolor: color.subtle, border: `1px solid ${color.divider}`, minHeight: 84 }} aria-live="polite">
          {previewLoading && !preview && (
            <>
              <Skeleton width="80%" sx={{ bgcolor: '#EEF1F5' }} />
              <Skeleton width="60%" sx={{ bgcolor: '#EEF1F5' }} />
            </>
          )}
          {previewError && <Typography sx={{ fontSize: 14, color: color.error }}>{previewError.message}</Typography>}
          {preview && (
            <>
              <Row label="Customer gets back" value={usd(preview.refund)} strong />
              <Row label="Taken back from your share" value={usd(preview.takenBack)} />
              {preview.addedToOwed > 0 && <Row label="Taken from your next payouts" value={usd(preview.addedToOwed)} />}
            </>
          )}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={refunding}
          sx={{ textTransform: 'none', fontWeight: 600, color: color.muted, borderRadius: radius.control, ...focusRing }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          disabled={!valid || refunding}
          onClick={submit}
          startIcon={refunding ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : null}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            borderRadius: radius.control,
            bgcolor: color.error,
            '&:hover': { bgcolor: '#C3173A' },
            ...focusRing,
          }}
        >
          {refunding ? 'Refunding…' : `Refund ${valid ? usd(parsed) : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
